import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Search, Download, Home, Menu, X, ChevronRight, Book, Users, MessageCircle, Globe } from 'lucide-react';
import { PDF_URL } from '../constants/appConfig';
import needsData from '../data/needs.json';
import feelingsData from '../data/feelings.json';
import chaptersData from '../data/chapters.json';
import rosenbergQuotes from '../data/rosenberg_quotes.json';
import images from '../data/images.json';
import specialSections from '../data/special_sections.json';
import ChapterView from './ChapterView';
import { CommunicationWheel, MutualStrategies, BasicFeelingsNeedsTable, CommunicationChecklist, OFNRPracticeSheets } from './InteractiveWorksheets';
import GrantSpiralOrb from './GrantSpiralOrb';

function WorkbookPro() {
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeSection, setActiveSection] = useState('overview');
  const [searchNeed, setSearchNeed] = useState('');
  const [searchFeeling, setSearchFeeling] = useState('');
  const [user, setUser] = useState(null);
  const [showSignup, setShowSignup] = useState(false);

  useEffect(() => {
    // Check if user exists
    const userId = localStorage.getItem('workbook_user_id');
    const userName = localStorage.getItem('workbook_user_name');
    const userEmail = localStorage.getItem('workbook_user_email');
    
    if (userId && userEmail) {
      setUser({ id: userId, name: userName, email: userEmail });
    } else {
      setShowSignup(true);
    }

    // Scroll tracking for active section
    const handleScroll = () => {
      const sections = ['overview', 'key-diagrams', 'core-pillars', 'quick-tools', 'interactive-worksheets'];
      const scrollPos = window.scrollY + 100;
      
      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const top = element.offsetTop;
          const bottom = top + element.offsetHeight;
          if (scrollPos >= top && scrollPos < bottom) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const createUser = async (name, email) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/workbook/users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email })
      });
      
      if (!response.ok) throw new Error('Failed to create user');
      
      const data = await response.json();
      localStorage.setItem('workbook_user_id', data.id);
      localStorage.setItem('workbook_user_name', data.name);
      localStorage.setItem('workbook_user_email', data.email);
      setUser({ id: data.id, name: data.name, email: data.email });
      setShowSignup(false);
    } catch (error) {
      alert('Failed to create account');
    }
  };

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  if (selectedChapter) {
    return <ChapterView chapter={selectedChapter} onBack={() => setSelectedChapter(null)} />;
  }

  const filteredNeeds = needsData.categories.map(cat => ({
    ...cat,
    needs: cat.needs.filter(need => 
      need.toLowerCase().includes(searchNeed.toLowerCase())
    )
  })).filter(cat => cat.needs.length > 0);

  const filteredFeelingsSatisfied = feelingsData.needsSatisfied.categories.map(cat => ({
    ...cat,
    feelings: cat.feelings.filter(feeling => 
      feeling.toLowerCase().includes(searchFeeling.toLowerCase())
    )
  })).filter(cat => cat.feelings.length > 0);

  const filteredFeelingsUnsatisfied = feelingsData.needsNotSatisfied.categories.map(cat => ({
    ...cat,
    feelings: cat.feelings.filter(feeling => 
      feeling.toLowerCase().includes(searchFeeling.toLowerCase())
    )
  })).filter(cat => cat.feelings.length > 0);

  return (
    <div className="flex min-h-screen bg-white">
      {/* Sidebar Navigation */}
      <aside 
        className={`fixed top-0 left-0 h-full border-r transition-all duration-300 overflow-y-auto z-50 ${
          sidebarOpen ? 'w-80' : 'w-0'
        }`}
        style={{ backgroundColor: 'hsl(138 20% 94%)' }}
      >
        <div className="p-6 space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold">Contents</h2>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setSidebarOpen(false)}
              className="md:hidden"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Navigation Menu */}
          <nav className="space-y-2">
            <button
              onClick={() => scrollToSection('overview')}
              className={`w-full text-left px-4 py-2 rounded transition-colors ${
                activeSection === 'overview' ? 'bg-black text-white' : 'hover:bg-gray-200'
              }`}
            >
              <Book className="w-4 h-4 inline mr-2" />
              Overview
            </button>

            <button
              onClick={() => scrollToSection('key-diagrams')}
              className={`w-full text-left px-4 py-2 rounded transition-colors ${
                activeSection === 'key-diagrams' ? 'bg-black text-white' : 'hover:bg-gray-200'
              }`}
            >
              <Book className="w-4 h-4 inline mr-2" />
              Key Diagrams
            </button>

            <button
              onClick={() => scrollToSection('core-pillars')}
              className={`w-full text-left px-4 py-2 rounded transition-colors ${
                activeSection === 'core-pillars' ? 'bg-black text-white' : 'hover:bg-gray-200'
              }`}
            >
              <Book className="w-4 h-4 inline mr-2" />
              Core Pillars
            </button>

            <button
              onClick={() => scrollToSection('quick-tools')}
              className={`w-full text-left px-4 py-2 rounded transition-colors ${
                activeSection === 'quick-tools' ? 'bg-black text-white' : 'hover:bg-gray-200'
              }`}
            >
              <Book className="w-4 h-4 inline mr-2" />
              Quick Reference
            </button>

            <button
              onClick={() => scrollToSection('interactive-worksheets')}
              className={`w-full text-left px-4 py-2 rounded transition-colors ${
                activeSection === 'interactive-worksheets' ? 'bg-black text-white' : 'hover:bg-gray-200'
              }`}
            >
              <Book className="w-4 h-4 inline mr-2" />
              Interactive Worksheets
            </button>

            <div className="pt-4 border-t">
              <p className="text-xs font-semibold text-gray-500 mb-2 px-4">CHAPTERS</p>
              {chaptersData.chapters.map((chapter) => (
                <button
                  key={chapter.id}
                  onClick={() => {
                    setSelectedChapter(chapter);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className="w-full text-left px-4 py-2 rounded hover:bg-gray-200 transition-colors group"
                >
                  <div className="flex justify-between items-center">
                    <span className="text-sm">
                      <span className="font-semibold">{chapter.id}.</span> {chapter.title}
                    </span>
                    <ChevronRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Pages {chapter.pages}</p>
                </button>
              ))}
            </div>

            <div className="pt-4 border-t">
              <p className="text-xs font-semibold text-gray-500 mb-2 px-4">PRACTICE TOOLS</p>
              <a href="/constellation" className="block w-full text-left px-4 py-2 rounded hover:bg-gray-200 transition-colors">
                <Users className="w-4 h-4 inline mr-2" />
                <span className="text-sm">Constellation Work</span>
                <span className="ml-1 text-[10px] bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded">BETA</span>
              </a>
              <a href="/partners" className="block w-full text-left px-4 py-2 rounded hover:bg-gray-200 transition-colors">
                <MessageCircle className="w-4 h-4 inline mr-2" />
                <span className="text-sm">Find Practice Partners</span>
                <span className="ml-1 text-[10px] bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded">BETA</span>
              </a>
              <a href="/planetary" className="block w-full text-left px-4 py-2 rounded hover:bg-gray-200 transition-colors">
                <Globe className="w-4 h-4 inline mr-2" />
                <span className="text-sm">PlanetaryFam Matching</span>
                <span className="ml-1 text-[10px] bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded">BETA</span>
              </a>
              <a href="/dashboard" className="block w-full text-left px-4 py-2 rounded hover:bg-gray-200 transition-colors">
                <Book className="w-4 h-4 inline mr-2" />
                <span className="text-sm">My Dashboard</span>
              </a>
            </div>
          </nav>
        </div>
      </aside>

      {/* Main Content */}
      <main className={`flex-1 transition-all duration-300 ${sidebarOpen ? 'ml-80' : 'ml-0'}`}>
        {/* Header */}
        <header className="sticky top-0 bg-white border-b px-6 py-4 z-40">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <Button 
                onClick={() => setSidebarOpen(!sidebarOpen)}
                variant="outline"
                size="sm"
                className="border-black"
                style={{ borderRadius: 0 }}
              >
                <Menu className="w-4 h-4 mr-2" />
                {sidebarOpen ? 'Hide' : 'Show'} Contents
              </Button>
              <div>
                <h1 className="text-2xl font-bold">The Belonging Sessions Interactive Workbook</h1>
                <p className="text-sm text-gray-600">Friends & Family NVC Basics by Steve Torma</p>
              </div>
            </div>
            <div className="flex gap-3">
              <a 
                href={PDF_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="workbook-pro-pdf-download"
              >
                <Button variant="outline" className="border-black" style={{ borderRadius: 0 }}>
                  <Download className="w-4 h-4 mr-2" />
                  Belonging eBook PDF
                </Button>
              </a>
              <a href="/dashboard">
                <Button variant="outline" className="border-black" style={{ borderRadius: 0 }}>
                  📊 Dashboard
                </Button>
              </a>
              <a href="/">
                <Button variant="outline" className="border-black" style={{ borderRadius: 0 }}>
                  <Home className="w-4 h-4 mr-2" />
                  Home
                </Button>
              </a>
            </div>
          </div>
        </header>

        {/* Content Sections */}
        <div className="px-6 py-12 max-w-6xl">
          {/* Overview */}
          <section id="overview" className="mb-20">
            <h2 className="text-5xl font-bold mb-6">Welcome to Your Interactive Workbook</h2>
            
            {/* Grant Spiral Orb with NVC Wisdom */}
            <GrantSpiralOrb 
              wisdom={rosenbergQuotes.quotes[Math.floor(Math.random() * rosenbergQuotes.quotes.length)].text}
              enabled={true}
            />
            
            <div className="space-y-4 text-lg text-gray-700">
              <p className="text-center italic text-gray-600 mb-6">
                This interactive workbook is a companion to Steve Torma's printed PDF. Use either or both to follow along with the course. 
                We're building this as we go — please share your feedback!
              </p>
              <p className="text-center text-sm text-gray-500 mb-8">
                💚 Your responses here create your private personal journal, shared only at your choosing.
              </p>
              <p>
                Steve has distilled decades of NVC practice into this comprehensive workbook. These exercises represent his wisdom from 40+ years 
                guiding communities toward compassionate communication and partnership culture.
              </p>
              <p>
                Use the <strong>sidebar navigation</strong> (left) to jump between sections, or scroll through to explore. 
                All your progress and reflections are automatically saved.
              </p>
              <div className="grid md:grid-cols-3 gap-4 mt-8">
                <Card style={{ borderRadius: 0 }}>
                  <CardContent className="p-6 text-center">
                    <p className="text-4xl font-bold mb-2">8</p>
                    <p className="text-sm text-gray-600">Chapters with Images</p>
                  </CardContent>
                </Card>
                <Card style={{ borderRadius: 0 }}>
                  <CardContent className="p-6 text-center">
                    <p className="text-4xl font-bold mb-2">5</p>
                    <p className="text-sm text-gray-600">Interactive Worksheets</p>
                  </CardContent>
                </Card>
                <Card style={{ borderRadius: 0 }}>
                  <CardContent className="p-6 text-center">
                    <p className="text-4xl font-bold mb-2">3</p>
                    <p className="text-sm text-gray-600">Practice Tools (BETA)</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </section>

          {/* Compassionate Communication Dialog Script */}
          <section className="mb-20">
            <Card style={{ borderRadius: 0, background: 'linear-gradient(135deg, rgba(0,0,0,0.03) 0%, rgba(0,0,0,0.01) 100%)', backdropFilter: 'blur(10px)', border: '2px solid black' }}>
              <CardHeader>
                <CardTitle className="text-4xl mb-2">A Compassionate Communication Dialog</CardTitle>
                <p className="text-lg text-gray-600">Follow this script for effective NVC conversations</p>
              </CardHeader>
              <CardContent className="space-y-4 text-lg">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <p className="font-bold text-2xl">1. Slow it down!</p>
                    <p className="font-bold text-2xl">2. Chunk it down!</p>
                    <p className="italic">"What I would like to tell you is (using OFNR & ≤40 words)..."</p>
                    <p>"Would you please tell me what you heard me say?"</p>
                    <p className="font-bold text-2xl">3. Reflect it back!</p>
                    <p>"Thank you for sharing that with me."</p>
                    <p>"What I heard you say was..."</p>
                    <p>"Is that right?" <span className="text-sm text-gray-600">(wait for response)</span></p>
                    <p>"Is there more?"</p>
                  </div>
                  <div className="space-y-3">
                    <p>"Thank you for hearing me."</p>
                    <p className="italic">"Yes, that's right and I would also like to say (using OFNR & ≤40 words)" OR "No, that's not quite it. What I am trying to say..."</p>
                    <p>"Would you please tell me what you heard me say?"</p>
                    <p className="font-bold text-2xl">4. Alternate speaking</p>
                    <p className="font-bold text-2xl">5. Express gratitude</p>
                    <p>"Thank you for hearing me. I'm complete for now."</p>
                    <p>"Would you please tell me what is going on for you having heard me say this?"</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </section>

          {/* Key Diagrams */}
          <section id="key-diagrams" className="mb-20">
            <h2 className="text-4xl md:text-6xl font-bold mb-12">Key Visual Diagrams</h2>
            
            {/* Featured: 9 Basic Needs */}
            <div className="mb-12">
              <Card style={{ borderRadius: 0, border: '3px solid black' }}>
                <CardHeader style={{ backgroundColor: 'hsl(138 25% 96%)' }}>
                  <CardTitle className="text-3xl">The 9 Basic Human Needs</CardTitle>
                  <p className="text-base text-gray-600">Page 5 • Foundation of NVC</p>
                </CardHeader>
                <CardContent className="p-8">
                  <img 
                    src={images.diagrams.nine_basic_needs.url} 
                    alt="9 Basic Human Needs"
                    className="w-full max-w-2xl mx-auto border border-gray-200 mb-4"
                  />
                  <p className="text-lg text-gray-700 text-center">{images.diagrams.nine_basic_needs.description}</p>
                </CardContent>
              </Card>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {/* Top Left: Our Internal Map of Reality */}
              <Card style={{ borderRadius: 0 }}>
                <CardHeader>
                  <CardTitle className="text-2xl">Our Internal Map of Reality</CardTitle>
                  <p className="text-sm text-gray-600">Pages 11-12</p>
                </CardHeader>
                <CardContent>
                  <img 
                    src={images.diagrams.dance_floors.url} 
                    alt="Internal Map of Reality"
                    className="w-full border border-gray-200 mb-4"
                  />
                  <p className="text-base text-gray-700">Communication flow between honesty and empathy</p>
                </CardContent>
              </Card>

              {/* Top Right: Our Quadrune Brain */}
              <Card style={{ borderRadius: 0 }}>
                <CardHeader>
                  <CardTitle className="text-2xl">Our Quadrune Brain</CardTitle>
                  <p className="text-sm text-gray-600">Page 10</p>
                </CardHeader>
                <CardContent>
                  <img 
                    src={images.diagrams.tree_of_life.url} 
                    alt="Our Quadrune Brain"
                    className="w-full border border-gray-200 mb-4"
                  />
                  <p className="text-base text-gray-700">Three focus options for connection</p>
                </CardContent>
              </Card>

              {/* Bottom Left: The Golden Rule */}
              <Card style={{ borderRadius: 0 }}>
                <CardHeader>
                  <CardTitle className="text-2xl">The Golden Rule Across Faiths</CardTitle>
                  <p className="text-sm text-gray-600">Page 9</p>
                </CardHeader>
                <CardContent>
                  <img 
                    src={images.diagrams.golden_rule.url} 
                    alt="The Golden Rule"
                    className="w-full border border-gray-200 mb-4"
                  />
                  <p className="text-base text-gray-700">Universal compassion across religions</p>
                </CardContent>
              </Card>

              {/* Bottom Right: 3 Areas of Attention */}
              <Card style={{ borderRadius: 0 }}>
                <CardHeader>
                  <CardTitle className="text-2xl">3 Areas of Attention</CardTitle>
                  <p className="text-sm text-gray-600">Page 48</p>
                </CardHeader>
                <CardContent>
                  <img 
                    src={images.diagrams.three_areas.url} 
                    alt="3 Areas of Attention"
                    className="w-full border border-gray-200 mb-4"
                  />
                  <p className="text-base text-gray-700">Body Sensations, Things Outside, Thoughts</p>
                </CardContent>
              </Card>
            </div>

            {/* Rosenberg Quote */}
            <div className="mt-12 p-8 bg-gradient-to-r from-gray-50 to-gray-100 border-l-4 border-black">
              <p className="text-2xl italic leading-relaxed text-gray-800">
                "When we focus on clarifying what is being observed, felt, and needed rather than on diagnosing and judging, we discover the depth of our own compassion."
              </p>
              <p className="text-lg font-semibold mt-4">— Marshall Rosenberg</p>
            </div>
          </section>

          {/* Core Pillars */}
          <section id="core-pillars" className="mb-20">
            <h2 className="text-4xl md:text-6xl font-bold mb-8">Core Pillars & Key Concepts</h2>
            <p className="text-xl text-gray-700 mb-12">
              These foundational concepts will be explored in depth during your live sessions with Steve Torma.
            </p>

            <div className="grid md:grid-cols-2 gap-8">
              {/* OFNR Framework - Enhanced */}
              <Card style={{ borderRadius: 0 }} className="bg-black text-white md:col-span-2">
                <CardHeader>
                  <CardTitle className="text-4xl mb-3">OFNR Framework</CardTitle>
                  <p className="text-lg text-gray-300">The heart of Nonviolent Communication</p>
                </CardHeader>
                <CardContent className="grid md:grid-cols-4 gap-6">
                  <div>
                    <h4 className="text-2xl font-bold mb-2">O</h4>
                    <p className="text-lg font-semibold mb-1">Observation</p>
                    <p className="text-base text-gray-300">Free from evaluation. "When I see, hear..."</p>
                  </div>
                  <div>
                    <h4 className="text-2xl font-bold mb-2">F</h4>
                    <p className="text-lg font-semibold mb-1">Feeling</p>
                    <p className="text-base text-gray-300">Emotions or bodily sensations, not thoughts</p>
                  </div>
                  <div>
                    <h4 className="text-2xl font-bold mb-2">N</h4>
                    <p className="text-lg font-semibold mb-1">Need</p>
                    <p className="text-base text-gray-300">Universal human needs, distinct from strategies</p>
                  </div>
                  <div>
                    <h4 className="text-2xl font-bold mb-2">R</h4>
                    <p className="text-lg font-semibold mb-1">Request</p>
                    <p className="text-base text-gray-300">Clear, specific, doable without demanding</p>
                  </div>
                </CardContent>
              </Card>

              {/* 5 Key Practices - PROMINENT */}
              <Card style={{ borderRadius: 0 }} className="bg-gray-900 text-white md:col-span-2">
                <CardHeader>
                  <CardTitle className="text-4xl mb-3">5 Key Practices for Compassionate Conversation</CardTitle>
                  <p className="text-lg text-gray-300">Master these for effective dialogue</p>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-5 gap-6">
                    <div className="space-y-2">
                      <div className="w-16 h-16 rounded-full bg-white text-black flex items-center justify-center text-3xl font-bold mx-auto">1</div>
                      <p className="font-bold text-xl text-center">Slow it down</p>
                      <p className="text-sm text-gray-300 text-center">Give space for connection</p>
                    </div>
                    <div className="space-y-2">
                      <div className="w-16 h-16 rounded-full bg-white text-black flex items-center justify-center text-3xl font-bold mx-auto">2</div>
                      <p className="font-bold text-xl text-center">Chunk it down</p>
                      <p className="text-sm text-gray-300 text-center">≤40 words at a time</p>
                    </div>
                    <div className="space-y-2">
                      <div className="w-16 h-16 rounded-full bg-white text-black flex items-center justify-center text-3xl font-bold mx-auto">3</div>
                      <p className="font-bold text-xl text-center">Reflect back</p>
                      <p className="text-sm text-gray-300 text-center">Confirm understanding</p>
                    </div>
                    <div className="space-y-2">
                      <div className="w-16 h-16 rounded-full bg-white text-black flex items-center justify-center text-3xl font-bold mx-auto">4</div>
                      <p className="font-bold text-xl text-center">Alternate speaking</p>
                      <p className="text-sm text-gray-300 text-center">Take turns fully</p>
                    </div>
                    <div className="space-y-2">
                      <div className="w-16 h-16 rounded-full bg-white text-black flex items-center justify-center text-3xl font-bold mx-auto">5</div>
                      <p className="font-bold text-xl text-center">Express gratitude</p>
                      <p className="text-sm text-gray-300 text-center">Acknowledge the gift</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Four D's */}
              <Card style={{ borderRadius: 0 }} className="bg-gray-50">
                <CardHeader>
                  <CardTitle className="text-2xl">The Four D's of Disconnection</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div><strong>Diagnosis:</strong> Judging, labeling, criticizing</div>
                  <div><strong>Denial of Responsibility:</strong> Blaming, denying choice</div>
                  <div><strong>Demand:</strong> Implies threat</div>
                  <div><strong>Deserve:</strong> Links behavior with punishment/reward</div>
                </CardContent>
              </Card>

              {/* Four Basic Choices */}
              <Card style={{ borderRadius: 0 }} className="bg-gray-50">
                <CardHeader>
                  <CardTitle className="text-2xl">Four Basic Choices</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div>1. Hear blame, blame back</div>
                  <div>2. Hear blame, blame self</div>
                  <div>3. Hear my own feelings & needs</div>
                  <div>4. Hear speaker's feelings & needs</div>
                </CardContent>
              </Card>

              {/* Jackal vs Giraffe */}
              <Card style={{ borderRadius: 0 }} className="bg-gray-50">
                <CardHeader>
                  <CardTitle className="text-2xl">Jackal vs. Giraffe Language</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div><strong>Jackal:</strong> Judgmental, blaming, critical</div>
                  <div><strong>Giraffe/Street Giraffe:</strong> Honest, natural NVC consciousness</div>
                </CardContent>
              </Card>

              {/* Key Principles */}
              <Card style={{ borderRadius: 0 }} className="bg-gray-50">
                <CardHeader>
                  <CardTitle className="text-2xl">Key NVC Principles</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div>• Connection before correction</div>
                  <div>• Feelings arise from needs</div>
                  <div>• Conflict is about strategies, not needs</div>
                  <div>• Requests allow for 'no'</div>
                  <div>• Universal human needs</div>
                  <div>• Take responsibility</div>
                </CardContent>
              </Card>
            </div>
          </section>

          {/* Quick Tools */}
          <section id="quick-tools" className="mb-20">
            <h2 className="text-4xl md:text-6xl font-bold mb-6">Quick Reference Tools</h2>
            <p className="text-xl text-gray-700 mb-12">Essential lists to support your NVC practice. Use these during conversations, exercises, or anytime you need to identify feelings and needs.</p>
            
            <Tabs defaultValue="needs" className="w-full">
              <TabsList className="mb-8 bg-gray-100 p-2">
                <TabsTrigger value="needs" className="text-lg px-8 py-4 data-[state=active]:bg-black data-[state=active]:text-white">
                  <div className="text-left">
                    <div className="font-bold">Needs List</div>
                    <div className="text-xs font-normal opacity-70">Universal human needs</div>
                  </div>
                </TabsTrigger>
                <TabsTrigger value="feelings" className="text-lg px-8 py-4 data-[state=active]:bg-black data-[state=active]:text-white">
                  <div className="text-left">
                    <div className="font-bold">Feelings List</div>
                    <div className="text-xs font-normal opacity-70">Emotional vocabulary</div>
                  </div>
                </TabsTrigger>
                <TabsTrigger value="ofnr" className="text-lg px-8 py-4 data-[state=active]:bg-black data-[state=active]:text-white">
                  <div className="text-left">
                    <div className="font-bold">OFNR Builder</div>
                    <div className="text-xs font-normal opacity-70">Statement creator</div>
                  </div>
                </TabsTrigger>
              </TabsList>

              {/* Needs List */}
              <TabsContent value="needs" className="space-y-8">
                <div className="bg-gray-50 p-6 rounded-sm mb-8">
                  <h3 className="text-2xl font-bold mb-3">About Needs</h3>
                  <p className="text-lg text-gray-700 leading-relaxed">
                    In NVC, <strong>needs</strong> are universal qualities that all humans share - things like safety, connection, autonomy, and meaning. 
                    They are different from <em>strategies</em> (specific ways to meet needs). When we connect with our needs and others' needs, conflict dissolves and creative solutions emerge.
                  </p>
                </div>
                <div className="max-w-2xl">
                  <div className="relative">
                    <Search className="absolute left-4 top-4 w-5 h-5 text-gray-400" />
                    <Input
                      placeholder="Search needs..."
                      value={searchNeed}
                      onChange={(e) => setSearchNeed(e.target.value)}
                      className="pl-12 py-6 text-lg border-black"
                      style={{ borderRadius: 0 }}
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-3 gap-8">
                  {filteredNeeds.map((category, idx) => (
                    <div key={idx} className="space-y-3">
                      <h3 className="text-2xl font-bold">{category.name}</h3>
                      <div className="space-y-2">
                        {category.needs.map((need, i) => (
                          <div key={i} className="text-lg text-gray-700 hover:text-black cursor-pointer transition-colors">
                            • {need}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </TabsContent>

              {/* Feelings List */}
              <TabsContent value="feelings" className="space-y-8">
                <div className="bg-gray-50 p-6 rounded-sm mb-8">
                  <h3 className="text-2xl font-bold mb-3">About Feelings</h3>
                  <p className="text-lg text-gray-700 leading-relaxed">
                    <strong>Feelings</strong> are our inner emotional and bodily sensations - distinct from thoughts. They tell us whether our needs are being met (satisfied) or not (unsatisfied). 
                    Expanding your feelings vocabulary helps you understand yourself and connect more deeply with others.
                  </p>
                </div>
                <div className="max-w-2xl">
                  <div className="relative">
                    <Search className="absolute left-4 top-4 w-5 h-5 text-gray-400" />
                    <Input
                      placeholder="Search feelings..."
                      value={searchFeeling}
                      onChange={(e) => setSearchFeeling(e.target.value)}
                      className="pl-12 py-6 text-lg border-black"
                      style={{ borderRadius: 0 }}
                    />
                  </div>
                </div>

                <div className="space-y-12">
                  <div>
                    <h3 className="text-3xl font-bold mb-6 text-green-700">When Needs Are Satisfied</h3>
                    <div className="grid md:grid-cols-4 gap-6">
                      {filteredFeelingsSatisfied.map((category, idx) => (
                        <div key={idx} className="space-y-2">
                          <h4 className="text-xl font-bold">{category.name}</h4>
                          <div className="space-y-1">
                            {category.feelings.map((feeling, i) => (
                              <div key={i} className="text-base text-gray-700 hover:text-black cursor-pointer transition-colors">
                                {feeling}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="text-3xl font-bold mb-6 text-red-700">When Needs Are Not Satisfied</h3>
                    <div className="grid md:grid-cols-4 gap-6">
                      {filteredFeelingsUnsatisfied.map((category, idx) => (
                        <div key={idx} className="space-y-2">
                          <h4 className="text-xl font-bold">{category.name}</h4>
                          <div className="space-y-1">
                            {category.feelings.map((feeling, i) => (
                              <div key={i} className="text-base text-gray-700 hover:text-black cursor-pointer transition-colors">
                                {feeling}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </TabsContent>

              {/* OFNR Builder */}
              <TabsContent value="ofnr">
                <OFNRBuilder />
              </TabsContent>
            </Tabs>
          </section>

          {/* Interactive Worksheets */}
          <section id="interactive-worksheets" className="mb-20">
            <h2 className="text-4xl md:text-6xl font-bold mb-8">Interactive Worksheets</h2>
            <p className="text-xl text-gray-700 mb-12">
              Digital versions of key exercises from Steve Torma's workbook - now interactive and easier to use than paper.
            </p>

            <Tabs defaultValue="table" className="w-full">
              <TabsList className="mb-8">
                <TabsTrigger value="table" className="text-lg px-6 py-3">Feelings/Needs Exercise</TabsTrigger>
                <TabsTrigger value="wheel" className="text-lg px-6 py-3">Communication Wheel</TabsTrigger>
                <TabsTrigger value="mutual" className="text-lg px-6 py-3">Mutual Strategies</TabsTrigger>
                <TabsTrigger value="ofnr" className="text-lg px-6 py-3">OFNR Practice</TabsTrigger>
                <TabsTrigger value="checklist" className="text-lg px-6 py-3">Checklist</TabsTrigger>
              </TabsList>

              <TabsContent value="table">
                <BasicFeelingsNeedsTable onSave={(data) => console.log('Table saved:', data)} savedData={{}} />
              </TabsContent>

              <TabsContent value="wheel">
                <CommunicationWheel onSave={(data) => console.log('Wheel saved:', data)} savedData={{}} />
              </TabsContent>

              <TabsContent value="mutual">
                <MutualStrategies onSave={(data) => console.log('Strategies saved:', data)} savedData={{}} />
              </TabsContent>

              <TabsContent value="ofnr">
                <OFNRPracticeSheets onSave={(data) => console.log('OFNR saved:', data)} savedData={{}} />
              </TabsContent>

              <TabsContent value="checklist">
                <CommunicationChecklist onSave={(data) => console.log('Checklist saved:', data)} savedData={{}} />
              </TabsContent>
            </Tabs>
          </section>

          {/* Chapters Grid */}
          <section className="mb-20">
            <h2 className="text-4xl md:text-6xl font-bold mb-12">Eight-Chapter Journey</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {chaptersData.chapters.map((chapter) => (
                <Card 
                  key={chapter.id}
                  className="cursor-pointer hover:shadow-lg transition-shadow"
                  style={{ borderRadius: 0 }}
                  onClick={() => setSelectedChapter(chapter)}
                >
                  {chapter.image_url && (
                    <img src={chapter.image_url} alt={chapter.title} className="w-full h-48 object-cover" />
                  )}
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Chapter {chapter.id} • Pages {chapter.pages}</p>
                        <CardTitle className="text-2xl mb-2">{chapter.title}</CardTitle>
                      </div>
                      <ChevronRight className="w-6 h-6 text-gray-400" />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-base text-gray-700 mb-4">{chapter.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {chapter.keyTopics.slice(0, 2).map((topic, i) => (
                        <span key={i} className="px-3 py-1 bg-gray-100 text-xs font-semibold">
                          {topic}
                        </span>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          {/* Bonus Resources */}
          <section className="mb-20">
            <h2 className="text-4xl md:text-6xl font-bold mb-12">Bonus Resources</h2>
            
            {/* 101 Magic Words */}
            <Card style={{ borderRadius: 0 }} className="mb-8 bg-gradient-to-br from-purple-50 to-blue-50">
              <CardHeader>
                <CardTitle className="text-3xl">{specialSections.special_sections.magic_words.title}</CardTitle>
                <p className="text-base text-gray-600">Pages {specialSections.special_sections.magic_words.pages}</p>
              </CardHeader>
              <CardContent>
                <p className="text-lg text-gray-700 mb-6">{specialSections.special_sections.magic_words.description}</p>
                
                {specialSections.special_sections.magic_words.categories.map((category, idx) => (
                  <div key={idx} className="mb-8">
                    <h3 className="text-2xl font-bold mb-4">{category.theme}</h3>
                    <div className="grid md:grid-cols-2 gap-3">
                      {category.phrases.map((phrase, i) => (
                        <div key={i} className="p-3 bg-white rounded hover:bg-gray-50 transition-colors">
                          <p className="text-base">"{phrase}"</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* 30 Daily Practices */}
            <Card style={{ borderRadius: 0 }} className="mb-8 bg-gradient-to-br from-green-50 to-yellow-50">
              <CardHeader>
                <CardTitle className="text-3xl">{specialSections.special_sections.daily_practices.title}</CardTitle>
                <p className="text-base text-gray-600">Pages {specialSections.special_sections.daily_practices.pages}</p>
              </CardHeader>
              <CardContent>
                <p className="text-lg text-gray-700 mb-6 italic">{specialSections.special_sections.daily_practices.description}</p>
                
                <div className="grid md:grid-cols-2 gap-4">
                  {specialSections.special_sections.daily_practices.practices.map((practice, i) => (
                    <div key={i} className="p-4 bg-white rounded border-l-4 border-green-600">
                      <div className="flex gap-3">
                        <span className="text-2xl font-bold text-green-600">{i + 1}</span>
                        <p className="text-base text-gray-800">{practice}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Mourning Process */}
            <Card style={{ borderRadius: 0 }} className="bg-gradient-to-br from-amber-50 to-orange-50">
              <CardHeader>
                <CardTitle className="text-3xl">{specialSections.special_sections.mourning_process.title}</CardTitle>
                <p className="text-base text-gray-600">Pages {specialSections.special_sections.mourning_process.pages}</p>
              </CardHeader>
              <CardContent>
                <p className="text-lg text-gray-700 mb-6">{specialSections.special_sections.mourning_process.description}</p>
                
                <div className="space-y-8">
                  <div>
                    <h3 className="text-2xl font-bold mb-4">Steps 1-6: Work with Yourself</h3>
                    {specialSections.special_sections.mourning_process.steps_self.map((step) => (
                      <div key={step.number} className="mb-6 p-4 bg-white rounded">
                        <p className="font-bold text-lg mb-2">Step {step.number}: {step.step}</p>
                        <p className="text-sm text-gray-600 italic mb-2">Example: {step.example}</p>
                      </div>
                    ))}
                  </div>
                  
                  <div>
                    <h3 className="text-2xl font-bold mb-4">Steps 7-11: Conversation with Others</h3>
                    {specialSections.special_sections.mourning_process.steps_with_others.map((step) => (
                      <div key={step.number} className="mb-6 p-4 bg-white rounded">
                        <p className="font-bold text-lg mb-2">Step {step.number}: {step.step}</p>
                        <p className="text-sm text-gray-600">{step.note}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </section>
        </div>
      </main>
    </div>
  );
}

// OFNR Builder Component (same as before)
function OFNRBuilder() {
  const [step, setStep] = useState(1);
  const [ofnr, setOfnr] = useState({
    observation: '',
    feeling: '',
    need: '',
    request: ''
  });

  const steps = [
    { num: 1, label: 'Observation', key: 'observation', prompt: 'What did you observe? (Facts only, no evaluation)', placeholder: 'When I saw/heard...' },
    { num: 2, label: 'Feeling', key: 'feeling', prompt: 'How did you feel?', placeholder: 'I felt...' },
    { num: 3, label: 'Need', key: 'need', prompt: 'What need was met or unmet?', placeholder: 'Because I need...' },
    { num: 4, label: 'Request', key: 'request', prompt: 'What specific request do you have?', placeholder: 'Would you be willing to...' }
  ];

  const currentStep = steps[step - 1];

  return (
    <div className="max-w-3xl">
      <h3 className="text-3xl font-bold mb-8">OFNR Statement Builder</h3>
      
      <div className="flex justify-between mb-12">
        {steps.map((s) => (
          <div key={s.num} className="flex items-center">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold ${
              step >= s.num ? 'bg-black text-white' : 'bg-gray-200 text-gray-500'
            }`}>
              {s.num}
            </div>
            <span className={`ml-3 text-sm font-semibold ${
              step >= s.num ? 'text-black' : 'text-gray-400'
            }`}>{s.label}</span>
          </div>
        ))}
      </div>

      <div className="space-y-6">
        <div>
          <h4 className="text-2xl font-bold mb-2">{currentStep.prompt}</h4>
          <Input
            value={ofnr[currentStep.key]}
            onChange={(e) => setOfnr({ ...ofnr, [currentStep.key]: e.target.value })}
            placeholder={currentStep.placeholder}
            className="text-xl py-6 border-black"
            style={{ borderRadius: 0 }}
          />
        </div>

        <div className="flex gap-4">
          {step > 1 && (
            <Button 
              onClick={() => setStep(step - 1)}
              variant="outline"
              className="border-black px-8 py-6 text-lg"
              style={{ borderRadius: 0 }}
            >
              Back
            </Button>
          )}
          {step < 4 ? (
            <Button 
              onClick={() => setStep(step + 1)}
              className="bg-black text-white hover:bg-black/90 px-8 py-6 text-lg"
              style={{ borderRadius: 0 }}
            >
              Next
            </Button>
          ) : (
            <Button 
              onClick={() => {
                navigator.clipboard.writeText(
                  `When ${ofnr.observation}, I feel ${ofnr.feeling} because I need ${ofnr.need}. Would you be willing to ${ofnr.request}?`
                );
                alert('OFNR statement copied to clipboard!');
              }}
              className="bg-black text-white hover:bg-black/90 px-8 py-6 text-lg"
              style={{ borderRadius: 0 }}
            >
              Copy Statement
            </Button>
          )}
        </div>
      </div>

      {step === 4 && ofnr.observation && ofnr.feeling && ofnr.need && ofnr.request && (
        <div className="mt-12 p-8 bg-gray-50">
          <h4 className="text-xl font-bold mb-4">Your Complete OFNR Statement:</h4>
          <p className="text-2xl leading-relaxed">
            When <strong>{ofnr.observation}</strong>, I feel <strong>{ofnr.feeling}</strong> because I need <strong>{ofnr.need}</strong>. Would you be willing to <strong>{ofnr.request}</strong>?
          </p>
        </div>
      )}
    </div>
  );
}

export default WorkbookPro;
